import { useCallback, useState } from 'react';
import { useApi, fetchApiRef } from '@backstage/frontend-plugin-api';
import { invalidateTracksCache } from './cache';

const errorFromResponse = async (
  response: Response,
  fallback: string,
): Promise<Error> => {
  try {
    const data = await response.json();
    return new Error(data.error ?? fallback);
  } catch {
    return new Error(fallback);
  }
};

/** Deletes a UI-managed Pulse track definition and its derived data. */
export const useDeleteTrack = () => {
  const { fetch } = useApi(fetchApiRef);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const remove = useCallback(
    async (trackId: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `plugin://pulse/tracks/${encodeURIComponent(trackId)}`,
          { method: 'DELETE' },
        );
        if (!response.ok) {
          throw await errorFromResponse(
            response,
            `Failed to delete track: ${response.status}`,
          );
        }
        invalidateTracksCache();
      } catch (error_) {
        const requestError = error_ as Error;
        setError(requestError);
        throw requestError;
      } finally {
        setLoading(false);
      }
    },
    [fetch],
  );

  return { remove, loading, error };
};
